import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Handshake, EnvelopeSimple, ArrowRight, CheckCircle, Microphone, NewspaperClipping, UsersThree, CaretDown, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Turnstile from "../components/Turnstile";
import Seo from "../components/Seo";

const TYPES = [
  { id: "general", label: "General inquiry", icon: EnvelopeSimple, blurb: "Press, partnerships, or anything else on your mind." },
  { id: "sponsorship", label: "Become a sponsor", icon: Handshake, blurb: "Reach senior fintech marketers across our 6 events / year.", highlight: true },
  { id: "speaking", label: "Speak at an event", icon: Microphone, blurb: "Share what you're shipping with our curated rooms." },
  { id: "press", label: "Press / media", icon: NewspaperClipping, blurb: "Quote requests, media credentials, podcast asks." },
  { id: "partnership", label: "Partnership", icon: UsersThree, blurb: "Collabs, co-marketing, content distribution." },
];

const Contact = () => {
  const [params] = useSearchParams();
  const initialType = useMemo(() => {
    const t = params.get("type");
    return TYPES.find((x) => x.id === t) ? t : "general";
  }, [params]);

  const [type, setType] = useState(initialType);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "", event_interest: [] });
  const [events, setEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  useEffect(() => { setType(initialType); }, [initialType]);
  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEventDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleEventInterest = (title) => {
    setForm((f) => ({
      ...f,
      event_interest: f.event_interest.includes(title)
        ? f.event_interest.filter((t) => t !== title)
        : [...f.event_interest, title],
    }));
  };

  const removeEventInterest = (title) => {
    setForm((f) => ({ ...f, event_interest: f.event_interest.filter((t) => t !== title) }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "At least 10 characters";
    if (process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken) e.turnstile = "Please complete the anti-spam check";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        event_interest: Array.isArray(form.event_interest) ? form.event_interest.join(", ") : form.event_interest,
        type,
        turnstile_token: turnstileToken || undefined,
      };
      await api.post("/contact", payload);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="grain min-h-screen flex items-center" data-testid="contact-success-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 w-full">
          <div className="hard-border bg-cyber-surface p-8 sm:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 blur-3xl pointer-events-none" />
            <div className="relative">
              <CheckCircle size={48} weight="duotone" className="text-cyber-green" />
              <div className="term-label mt-6">/ message received</div>
              <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl mt-3 leading-[0.95]">
                Thanks, {form.name.split(" ")[0]}.<br />
                <span className="text-white">We'll reply within 48 hours.</span>
              </h1>
              <p className="text-white/70 mt-6 text-lg max-w-xl">
                {type === "sponsorship"
                  ? "A sponsorship lead from our team will follow up with the event brief, audience demographics, and tier options."
                  : "Someone from the FintechOmni team will be in touch shortly. Most replies happen within one working day."}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/" className="btn-neon" data-testid="contact-success-home">Back to home</Link>
                <Link to="/news" className="btn-ghost-neon" data-testid="contact-success-news">Read the newsroom</Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="grain min-h-screen cyber" data-testid="contact-page">
      <Seo
        path="/contact"
        title="Contact us — Sponsorships, speaking & partnerships"
        description="Talk to FintechOmni — sponsorship inquiries, speaking proposals, press requests, and partnerships. Replies within 48 hours."
        keywords="contact fintechomni, fintech sponsorship, sponsor fintech event, fintech speaker, fintech partnership"
      />
      {/* HERO */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-10 sm:pb-12">
          <div className="term-label mb-4">/ contact</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <h1 className="lg:col-span-7 font-display font-black tracking-tighter text-[44px] sm:text-7xl lg:text-8xl leading-[0.95] sm:leading-[0.9]">
              Talk to us.
            </h1>
            <p className="lg:col-span-5 text-white/70 text-base sm:text-lg max-w-xl">
              Sponsoring, speaking, partnering, or just curious. Whichever it is, the form below routes directly to a human.
            </p>
          </div>
        </div>
      </section>

      {/* TYPE SELECTOR */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
          <div className="term-label mb-5">/ what's this about?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const isActive = type === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  data-testid={`contact-type-${t.id}`}
                  className={`group relative hard-border p-5 text-left transition-colors duration-150 ${
                    isActive
                      ? "bg-white text-cyber-black border-white"
                      : t.highlight
                        ? "bg-cyber-elevated border-cyber-green hover:bg-cyber-surface"
                        : "bg-cyber-surface hover:bg-cyber-elevated"
                  }`}
                >
                  <Icon size={22} weight="duotone" className={isActive ? "text-cyber-black" : t.highlight ? "text-cyber-green" : "text-white"} />
                  <div className={`font-display font-black text-xl mt-3 ${isActive ? "text-cyber-black" : "text-white"}`}>{t.label}</div>
                  <p className={`text-xs mt-2 leading-relaxed ${isActive ? "text-cyber-black/70" : "text-white/55"}`}>{t.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPONSORSHIP CALLOUT (only when sponsorship selected) */}
      {type === "sponsorship" && (
        <section className="border-b border-cyber-line bg-cyber-elevated" data-testid="sponsorship-callout">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-1"><Handshake size={40} weight="duotone" className="text-cyber-green" /></div>
            <div className="lg:col-span-11">
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green">/ sponsorship desk</div>
              <h2 className="font-display font-black tracking-tighter text-2xl sm:text-3xl mt-2 leading-tight">
                Three tiers. Curated audience. Real ROI receipts.
              </h2>
              <p className="text-white/70 text-sm mt-2 max-w-3xl">
                Tell us which event(s) you're interested in and we'll send a brief deck with audience demographics, attendee personas, and tier inclusions within 24 hours.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* FORM */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <form onSubmit={submit} className="space-y-6 hard-border bg-cyber-surface p-5 sm:p-12 cyber">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name" required error={errors.name}>
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3" />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3" />
              </Field>
              <Field label="Company">
                <input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full px-4 py-3" />
              </Field>
              <Field label="Role / title">
                <input data-testid="contact-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-3" />
              </Field>
            </div>

            {type === "sponsorship" && (
              <div className="border-t border-cyber-line pt-6">
                <Field label="Event(s) of interest — select one or more">
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setEventDropdownOpen((o) => !o)}
                      data-testid="contact-event-interest-toggle"
                      className="w-full px-4 py-3 bg-cyber-black border border-cyber-line text-left flex items-center justify-between hover:border-cyber-green transition-colors"
                    >
                      <span className={form.event_interest.length ? "text-white" : "text-white/40"}>
                        {form.event_interest.length
                          ? `${form.event_interest.length} event${form.event_interest.length > 1 ? "s" : ""} selected`
                          : "Choose events..."}
                      </span>
                      <CaretDown size={16} weight="bold" className={`transition-transform ${eventDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {eventDropdownOpen && (
                      <div className="absolute z-20 left-0 right-0 mt-1 bg-cyber-black border border-cyber-green max-h-72 overflow-y-auto shadow-2xl" data-testid="contact-event-interest-dropdown">
                        {events.length === 0 ? (
                          <div className="px-4 py-3 text-white/50 text-sm font-mono-tech">No events available.</div>
                        ) : (
                          events.map((ev) => {
                            const isChecked = form.event_interest.includes(ev.title);
                            return (
                              <label
                                key={ev.id || ev.slug || ev.title}
                                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-cyber-elevated transition-colors border-b border-cyber-line/50 last:border-b-0 ${isChecked ? "bg-cyber-elevated" : ""}`}
                                data-testid={`contact-event-option-${ev.slug || ev.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleEventInterest(ev.title)}
                                  className="mt-1 accent-cyber-green"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-white text-sm font-semibold truncate">{ev.title}</div>
                                  {ev.location && <div className="text-white/50 text-xs mt-0.5 font-mono-tech">{ev.location}</div>}
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {form.event_interest.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="contact-event-interest-chips">
                      {form.event_interest.map((title) => (
                        <span
                          key={title}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyber-green/10 border border-cyber-green text-cyber-green text-xs font-mono-tech"
                        >
                          {title}
                          <button
                            type="button"
                            onClick={() => removeEventInterest(title)}
                            className="hover:text-white transition-colors"
                            data-testid={`contact-event-chip-remove-${title}`}
                            aria-label={`Remove ${title}`}
                          >
                            <X size={12} weight="bold" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </Field>
              </div>
            )}

            <Field label={type === "sponsorship" ? "Tell us about your goals" : "Message"} required error={errors.message}>
              <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} placeholder={type === "sponsorship" ? "What audience are you trying to reach? What does success look like?" : "Anything you'd like us to know."} className="w-full px-4 py-3" />
            </Field>

            <div data-testid="contact-turnstile-wrap">
              <Turnstile ref={turnstileRef} onToken={setTurnstileToken} action="contact" />
              {errors.turnstile && (
                <div className="mt-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red">/ {errors.turnstile}</div>
              )}
            </div>

            {error && <div className="hard-border border-cyber-red text-cyber-red p-4 font-mono-tech text-xs" data-testid="contact-error">/ ERROR :: {error}</div>}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-cyber-line">
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/40">
                / replies within 48h
              </p>
              <button type="submit" disabled={submitting} className="btn-neon w-full sm:w-auto justify-center" data-testid="contact-submit-btn">
                {submitting ? "Sending..." : (type === "sponsorship" ? "Request sponsor brief" : "Send message")}
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, required, error, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/80">
        {label} {required && <span className="text-cyber-green">*</span>}
      </label>
      {error && <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red">{error}</span>}
    </div>
    {children}
  </div>
);

export default Contact;
